import React, { useState, useCallback, useEffect } from 'react';
import { Routes, Route, NavLink, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, ArrowLeftRight, History, Settings } from 'lucide-react';
import './App.css';

// Components
import BalancesDashboard from './components/BalancesDashboard';
import TransfersPage from './components/TransfersPage';
import TransferHistory from './components/TransferHistory';
import AdminPage from './components/AdminPage';
import Login from './components/Login';
import AccountSelector from './components/AccountSelector';
import DocsPage from './components/DocsPage';

// Hooks
import { useHealth } from './hooks/useHealth';
import { useAuth } from './auth/AuthContext';
import { useTimezone, COMMON_TIMEZONES } from './contexts/TimezoneContext';
import { bootstrapAccount, listAccounts, setAccountId, setAuthToken } from './services/apiClient';

const TABS = {
  DASHBOARD: 'dashboard',
  TRANSFERS: 'transfers',
  HISTORY: 'history',
  ADMIN: 'admin',
  DOCS: 'docs',
};

function App() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [adminInitialTab, setAdminInitialTab] = useState(null);
  const [showCreateAccount, setShowCreateAccount] = useState(false);
  const [suggestedAccountName, setSuggestedAccountName] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState("");
  const [appReady, setAppReady] = useState(false); // Track when app is fully initialized
  const [initError, setInitError] = useState(null);
  const { activeTimezone, userTimezone, setOverride, clearOverride, hasOverride } = useTimezone();
  
  // Only check health after app is ready (auth + accounts loaded)
  const { isConnected, isConnecting, isLoading: healthLoading } = useHealth(appReady ? 30000 : null);

  // Get active tab from URL path
  const getActiveTabFromPath = () => {
    const path = location.pathname.toLowerCase();
    if (path === '/transfers') return TABS.TRANSFERS;
    if (path === '/history') return TABS.HISTORY;
    if (path === '/admin') return TABS.ADMIN;
    if (path === '/docs') return TABS.DOCS;
    return TABS.DASHBOARD;
  };
  const activeTab = getActiveTabFromPath();

  // Handle transfer selection from history
  const handleSelectTransfer = useCallback((transferId) => {
    navigate('/transfers');
  }, [navigate]);

  // Reload accounts (e.g., after creating/deleting)
  const reloadAccounts = useCallback(async () => {
    try {
      const response = await listAccounts();
      const nextAccounts = response.accounts || [];
      setAccounts(nextAccounts);
      // If current account was deleted, switch to first available
      if (nextAccounts.length > 0 && !nextAccounts.find(a => a.id === selectedAccountId)) {
        setSelectedAccountId(nextAccounts[0].id);
      } else if (nextAccounts.length === 0) {
        setSelectedAccountId("");
      }
    } catch (error) {
      console.error("Failed to reload accounts:", error);
    }
  }, [selectedAccountId]);

  // Handle account created
  const handleAccountCreated = useCallback((account) => {
    reloadAccounts();
    setSelectedAccountId(account.id);
    setShowCreateAccount(false);
    setSuggestedAccountName(null); // Clear suggested name after creation
    navigate('/dashboard');
  }, [reloadAccounts, navigate]);

  // Get connection status
  const getConnectionStatus = () => {
    if (!appReady) return { color: 'orange', text: 'Initializing...' };
    if (healthLoading || isConnecting) return { color: 'orange', text: 'Connecting...' };
    if (isConnected) return { color: 'green', text: 'Connected' };
    return { color: 'red', text: 'Disconnected' };
  };

  const connectionStatus = getConnectionStatus();

  // Initialize user: get token, load accounts - runs once when user changes
  useEffect(() => {
    // Reset state when user logs out
    if (!user) {
      setAuthToken(null);
      setAccounts([]);
      setSelectedAccountId("");
      setAppReady(false);
      setInitError(null);
      return;
    }

    let isActive = true;
    setAppReady(false);
    setInitError(null);
    
    const initializeUser = async () => {
      try {
        // Get fresh token
        const token = await user.getIdToken(true);
        if (!isActive) return;
        
        console.log('[App] Setting auth token, length:', token?.length);
        setAuthToken(token);
        
        // Ensure token is set before making API calls
        // Wait a bit longer to ensure the interceptor picks it up
        await new Promise(resolve => setTimeout(resolve, 100));
        if (!isActive) return;
        
        // Verify token is set
        console.log('[App] Making bootstrap call with token set');
        
        // Bootstrap and load accounts
        const bootstrap = await bootstrapAccount();
        if (!isActive) return;
        
        const response = await listAccounts();
        if (!isActive) return;
        
        const nextAccounts = response.accounts || [];
        setAccounts(nextAccounts);
        
        const preferredAccountId = bootstrap.account?.id || nextAccounts[0]?.id || "";
        console.log('[App] Bootstrap result:', { 
          bootstrapAccountId: bootstrap.account?.id,
          firstAccountId: nextAccounts[0]?.id,
          preferredAccountId,
          accountsCount: nextAccounts.length
        });
        setSelectedAccountId(preferredAccountId);
        
        // Set account ID in apiClient
        if (preferredAccountId) {
          console.log('[App] Setting account ID in apiClient:', preferredAccountId);
          setAccountId(preferredAccountId);
        } else {
          console.warn('[App] No account ID to set - user may have no accounts');
        }
        
        // Check if this is a signup flow or new user with no accounts
        const isSignupFlow = sessionStorage.getItem('signupRedirect') === 'true';
        const isNewUser = !bootstrap.hasAccounts && nextAccounts.length === 0;
        
        // Only show create account modal if:
        // 1. This is a signup flow AND user has no accounts, OR
        // 2. User has no accounts (new user)
        if ((isSignupFlow && nextAccounts.length === 0) || isNewUser) {
          // Clear the flag
          sessionStorage.removeItem('signupRedirect');
          // Capture suggested account name if provided
          if (bootstrap.suggestedAccountName) {
            setSuggestedAccountName(bootstrap.suggestedAccountName);
          }
          // Navigate to Admin page with Account tab and create modal open
          navigate('/admin');
          setAdminInitialTab('account');
          setShowCreateAccount(true);
        } else {
          // Existing user signing in - clear any leftover signupRedirect flag
          sessionStorage.removeItem('signupRedirect');
          // Stay on current page or default to dashboard
          if (location.pathname === '/' || location.pathname === '/login') {
            navigate('/dashboard');
          }
        }
        
        // Mark app as ready - this triggers the main UI render
        setAppReady(true);
      } catch (error) {
        console.error("Failed to initialize user:", error);
        setInitError(error.response?.data?.error || error.message || "Failed to initialize");
        // Still mark as ready so user can see error state
        setAppReady(true);
      }
    };

    initializeUser();

    return () => {
      isActive = false;
    };
  }, [user]);

  // Update account ID in apiClient when selection changes
  useEffect(() => {
    if (selectedAccountId) {
      setAccountId(selectedAccountId);
    }
  }, [selectedAccountId]);

  // Loading state: Firebase auth is checking
  if (authLoading) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in: show login
  if (!user) {
    return <Login />;
  }

  // User logged in but app not ready: show initialization loading
  if (!appReady) {
    return (
      <div className="App">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Initializing...</p>
        </div>
      </div>
    );
  }

  // App ready: render main UI
  return (
    <div className="App">
      {/* Header */}
      <header className="App-header">
        <div className="header-content">
          <h1 className="header-title">Fund Transfer Service</h1>
          
          {/* Desktop Navigation Only */}
          <div className="header-right desktop-only">
            {/* Navigation Tabs */}
            <nav className="nav-tabs">
              <NavLink
                to="/dashboard"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                onClick={() => setAdminInitialTab(null)}
              >
                Dashboard
              </NavLink>
              <NavLink
                to="/transfers"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                onClick={() => setAdminInitialTab(null)}
              >
                Transfers
              </NavLink>
              <NavLink
                to="/history"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                onClick={() => setAdminInitialTab(null)}
              >
                History
              </NavLink>
              <NavLink
                to="/admin"
                className={({ isActive }) => `nav-tab ${isActive ? 'active' : ''}`}
                onClick={() => { setAdminInitialTab(null); setShowCreateAccount(false); }}
              >
                Admin
              </NavLink>
            </nav>

            {/* Connection Status */}
            <div className="connection-status">
              <span className={`status-dot ${connectionStatus.color}`}></span>
              <span className="status-text">{connectionStatus.text}</span>
            </div>

            {accounts.length > 0 && (
              <AccountSelector
                accounts={accounts}
                selectedAccountId={selectedAccountId}
                onSelect={setSelectedAccountId}
                onManageAccount={() => {
                  setAdminInitialTab('account');
                  navigate('/admin');
                }}
              />
            )}

            <div className="user-menu">
              <span className="user-email">{user.email}</span>
              <button className="logout-button" onClick={logout}>Logout</button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-bottom-nav">
        <NavLink
          to="/dashboard"
          className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
          onClick={() => setAdminInitialTab(null)}
        >
          <LayoutDashboard size={20} />
          <span>Dashboard</span>
        </NavLink>
        <NavLink
          to="/transfers"
          className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
          onClick={() => setAdminInitialTab(null)}
        >
          <ArrowLeftRight size={20} />
          <span>Transfers</span>
        </NavLink>
        <NavLink
          to="/history"
          className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
          onClick={() => setAdminInitialTab(null)}
        >
          <History size={20} />
          <span>History</span>
        </NavLink>
        <NavLink
          to="/admin"
          className={({ isActive }) => `bottom-nav-item ${isActive ? 'active' : ''}`}
          onClick={() => { setAdminInitialTab(null); setShowCreateAccount(false); }}
        >
          <Settings size={20} />
          <span>Settings</span>
        </NavLink>
      </nav>

      {/* Error Banner */}
      {initError && (
        <div className="error-banner-global">
          ⚠️ {initError}
        </div>
      )}

      {/* No Account Warning - only show if not in signup flow */}
      {!selectedAccountId && accounts.length === 0 && !initError && !showCreateAccount && (
        <div className="warning-banner-global">
          ⚠️ No account access. Please contact an administrator to add you to an account.
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        <Routes>
          {/* Admin routes - accessible even without account for signup flow */}
          <Route 
            path="/admin/*" 
            element={
              <div className="admin-view">
                <AdminPage
                  accountId={selectedAccountId}
                  onAccountChange={reloadAccounts}
                  onAccountCreated={handleAccountCreated}
                  initialTab={adminInitialTab}
                  showCreateAccount={showCreateAccount}
                  suggestedAccountName={suggestedAccountName}
                />
              </div>
            } 
          />
          
          {/* Protected routes - require account */}
          <Route 
            path="/dashboard" 
            element={
              selectedAccountId ? (
                <div className="dashboard-view">
                  <BalancesDashboard />
                </div>
              ) : (
                <div className="no-account-state">
                  <p>Please wait for account access or contact an administrator.</p>
                </div>
              )
            } 
          />
          
          <Route 
            path="/transfers" 
            element={
              selectedAccountId ? (
                <div className="transfers-view">
                  <TransfersPage onNavigateToHistory={() => navigate('/history')} />
                </div>
              ) : <Navigate to="/dashboard" replace />
            } 
          />
          
          <Route 
            path="/history" 
            element={
              selectedAccountId ? (
                <div className="history-view">
                  <TransferHistory onSelectTransfer={handleSelectTransfer} />
                </div>
              ) : <Navigate to="/dashboard" replace />
            } 
          />
          
          <Route 
            path="/docs" 
            element={
              <div className="docs-view">
                <DocsPage />
              </div>
            } 
          />
          
          {/* Default route - redirect to dashboard */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </main>

      {/* Footer */}
      <footer className="App-footer">
        <div className="footer-content">
          <span>Fund Transfer Orchestration Service</span>
          <span>•</span>
          <NavLink
            to="/docs"
            className="footer-link"
            onClick={() => setAdminInitialTab(null)}
          >
            Docs
          </NavLink>
          <span>•</span>
          <span>API: {isConnecting ? 'Connecting...' : (isConnected ? 'Connected' : 'Disconnected')}</span>
          <span>•</span>
          <div className="footer-timezone">
            <label htmlFor="timezone-select" className="footer-timezone-label">Timezone:</label>
            <select
              id="timezone-select"
              value={activeTimezone}
              onChange={(e) => {
                const newTz = e.target.value;
                if (newTz === userTimezone) {
                  clearOverride();
                } else {
                  setOverride(newTz);
                }
              }}
              className="footer-timezone-select"
              title={hasOverride ? `Override: ${activeTimezone} (Default: ${userTimezone})` : `Default: ${activeTimezone}`}
            >
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
            {hasOverride && (
              <button
                onClick={clearOverride}
                className="footer-timezone-reset"
                title="Reset to default timezone"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
